import { useEffect } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useCourses } from '@hooks';
import { useAuth } from '@context';
import { ROUTES } from '@config/routes';
import { getViewableCourses, hasAdminPrivileges } from '@utils/permissions';

/**
 * Home Page
 * Lists available courses
 * Admins are automatically redirected to /admin/courses
 */
export function HomePage() {
  const { courses, loading, error } = useCourses();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Redirect admins and course managers to /admin/courses
  useEffect(() => {
    if (user && hasAdminPrivileges(user)) {
      navigate(ROUTES.ADMIN_COURSES, { replace: true });
    }
  }, [user, navigate]);

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading courses...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Card className="text-center">
          <Card.Body className="py-5">
            <h3 className="text-danger">Error Loading Courses</h3>
            <p>{error}</p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  const visibleCourses = getViewableCourses(user, courses);

  return (
    <Container className="home-page py-5">
      <Row className="mb-5">
        <Col>
          <div className="text-center">
            <h1 className="display-4 mb-3">Adaptive Learning Platform</h1>
            <p className="lead text-muted">
              Personalized learning that adapts to your progress
            </p>
          </div>
        </Col>
      </Row>

      <Row>
        <Col>
          <h2 className="mb-4">Available Courses</h2>
        </Col>
      </Row>

      {visibleCourses.length === 0 ? (
        <Row>
          <Col>
            <Card className="text-center">
              <Card.Body className="py-5">
                <h4>No Courses Available</h4>
                <p className="text-muted">
                  Check back later for new courses!
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      ) : (
        <Row>
          {visibleCourses.map((course) => (
            <Col key={course.id} md={6} lg={4} className="mb-4">
              <Card className="h-100 course-card">
                <Card.Body>
                  <Card.Title>{course.title}</Card.Title>
                  <Card.Text className="text-muted">
                    {course.description}
                  </Card.Text>
                  <div className="course-stats mb-3">
                    <small className="text-muted">
                      {course.sections?.length || 0} Sections
                    </small>
                  </div>
                </Card.Body>
                <Card.Footer className="bg-white border-0">
                  <Link 
                    to={ROUTES.COURSE_OVERVIEW.replace(':courseId', course.id)}
                    className="btn btn-primary w-100"
                  >
                    View Course
                  </Link>
                </Card.Footer>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </Container>
  );
}
